let ctx = null;
let volume = 0.22;

// Intensidade GERAL da preferência do usuário (0..1).
// Ex: 0 = silêncio total forçado, 0.5 = metade, 1 = usar valor "volume" acima.
// Esta variável é um OVERRIDE independente do "volume" de base (0.22).
// CAMADA DUPLA: volume ajusta a intensidade base do som de chat interno
// (0.22), notificationVolume01 ajusta a preferência do usuário.
// Os dois são MULTIPLICADOS no momento exato do play.
//
// DEFAULT INICIAL SEGURO = 0 (MUDO TOTAL).
//   Motivo: nos primeiros 200ms de inicialização do app o fetch
//   /api/auth/me ainda não voltou, então ainda NÃO SABEMOS qual é o
//   notificationVolume real do usuário. Se default fosse 1 (= 100%),
//   qualquer mensagem / chat interno que chegasse nessa janela iria
//   tocar a TODO volume mesmo que o usuário esteja em "mudo".
//   Começando em 0, a página inicializa sem apitar NADA; em seguida o
//   useAuth chama setChatInternoNotifVolume01(fator) / ou o localStorage
//   (abaixo) aplica o valor correto.
let notificationVolume01 = 0;

// Leitura AUTÔNOMA de localStorage na inicialização do módulo.
// Mesmo ANTES do React / useAuth montar, este módulo (que é o primeiro
// a ser importado em qualquer componente de chat interno) já sabe qual
// valor usar. Resolve a "race de inicialização" dos primeiros 500ms.
(function aplicarLocalStorageNoStartup() {
	try {
		if (typeof window === "undefined" || !window.localStorage) return;
		const prefixo = "PERABIT_NOTIF_VOLUME_v1_";
		const todas = Object.keys(window.localStorage).filter(k => k && k.startsWith(prefixo));
		if (todas.length === 0) return; // sem nenhuma preferência salva: fica 0 mudo temporário
		// Pega a chave mais recente (última por ordem alfabética = userId maior geralmente,
		// mas mesmo que pegue qualquer usuário neste PC, já está melhor que 100% default
		// e em seguida o useAuth vai sobrescrever com o userId correto mesmo).
		const chave = todas[todas.length - 1];
		const raw = window.localStorage.getItem(chave);
		if (!raw) return;
		const n = Number(raw);
		if (!Number.isFinite(n)) return;
		const clamp = Math.max(0, Math.min(100, n));
		notificationVolume01 = clamp / 100;
	} catch (_e) { /* noop: privado / storage bloqueado etc. Fica mudo 0. */ }
})();

const pegarCtx = () => {
	if (typeof window === "undefined") return null;
	if (!ctx) {
		const AC = window.AudioContext || window.webkitAudioContext;
		if (!AC) return null;
		ctx = new AC();
	}
	if (ctx.state === "suspended") {
		ctx.resume().catch(() => undefined);
	}
	return ctx;
};

// PROTEÇÃO 2 CAMADAS:
//   intensidade FINAL = (volume base do som 0.22) * notificationVolume01
//   ex1: usuario slider 0%  → 0.22 * 0 = 0 (SILENCIO ABSOLUTO)
//   ex2: usuario slider 50% → 0.22 * 0.5 = 0.11
//   ex3: usuario slider 100% → 0.22 * 1 = 0.22 (padrão original)
const bip = (freq, duracaoMs = 120, inicioDelayMs = 0, ganho = volume) => {
	const ganhoFinal = Math.max(0, Math.min(1, Number(ganho || 0) * Number(notificationVolume01 || 0)));
	// THRESHOLD PARANOICO 1% (0.01): abaixo disso não tocamos nada.
	// Evita bugs de arredondamento do Web Audio API / howler em que
	// valores muito pequenos ou 0 exato são arredondados para 100%.
	if (ganhoFinal <= 0.01) return;

	const c = pegarCtx();
	if (!c) return;
	const o = c.createOscillator();
	const g = c.createGain();
	o.type = "sine";
	o.frequency.value = freq;
	const agora = c.currentTime + inicioDelayMs / 1000;
	const fimSec = duracaoMs / 1000;
	g.gain.setValueAtTime(0.0001, agora);
	g.gain.exponentialRampToValueAtTime(ganhoFinal, agora + 0.015);
	g.gain.setValueAtTime(ganhoFinal, agora + fimSec - 0.03);
	g.gain.exponentialRampToValueAtTime(0.0001, agora + fimSec);
	o.connect(g);
	g.connect(c.destination);
	o.start(agora);
	o.stop(agora + fimSec + 0.02);
	setTimeout(() => {
		try { o.disconnect(); } catch { /* no-op */ }
		try { g.disconnect(); } catch { /* no-op */ }
	}, duracaoMs + inicioDelayMs + 80);
};

// Som DIFERENCIADO do ticket (sound.mp3) e do ringtone.
// WhatsApp "pip-pip" agudo: 880Hz 110ms -> silêncio 90ms -> 660Hz 110ms.
// É um som curto, agradável, fácil de distinguir.
export const tocarSomNovoChatInterno = () => {
	bip(880, 110, 0, volume);
	bip(660, 110, 200, volume * 0.9);
};

// Feedback sutil de envio: um só pip mais baixo.
export const tocarSomEnviadoChatInterno = () => {
	bip(520, 70, 0, volume * 0.5);
};

export const setVolumeChatInterno = (v) => {
	volume = Math.max(0, Math.min(1, Number(v) || 0));
};

// Preferência INDIVIDUAL do usuário por intensidade geral 0..100%.
// Chamado de useAuth.js sempre que user.notificationVolume mudar (login,
// socket user update, ajuste slider local, modal perfil salvar etc.).
// Esta função é idempotente e leve (só atualiza 1 número em memória).
export const setChatInternoNotifVolume01 = (fator01, { userId = null } = {}) => {
	notificationVolume01 = Math.max(0, Math.min(1, Number(fator01) || 0));
	// GRAVA LOCALSTORAGE para a inicialização do módulo no próximo F5
	// já iniciar com o valor certo (sem a janela de "mudo temporário" até
	// o useAuth rodar). Mesmo padrão usado no gravarLsVolume do useAuth.
	try {
		if (!userId || typeof window === "undefined" || !window.localStorage) return;
		const chave = `PERABIT_NOTIF_VOLUME_v1_${Number(userId) || 0}`;
		const comoPercentual = Math.max(0, Math.min(100, Math.round(notificationVolume01 * 100)));
		window.localStorage.setItem(chave, String(comoPercentual));
	} catch (_e) { /* noop */ }
};

// Getter público (usado por InternalChatWidget para checar mudo NA HORA
// da Notification API do Chrome). Retorna 0..1 como salvo no módulo.
export const getChatInternoNotifVolume01 = () => notificationVolume01;

// =============================================================================
// 🔥 GARANTIA NUCLEAR — MONKEY PATCH GLOBAL 🔥
// =============================================================================
// Mesmo que no futuro alguém adicione um useSound novo, um new Audio()
// genérico ou qualquer outro player de som que o time não lembre de
// passar pelo threshold, este patch BLOQUEIA A INVOCACAO NO NIVEL DA
// LINGUAGEM.
//
// Regra aplicada: se notificationVolume01 (global do módulo) estiver
// <= 0.01 (abaixo de 1%) → NÃO toca NADA, retorna Promise.resolve()
// vazio sem invocar o play() real.
//
// NOTA 1: NÃO ALTERA a biblioteca whaileys+6.5.1.patch (restrição ativa).
// NOTA 2: NÃO altera AudioContext (usado por bip() próprio deste módulo
// que já tem threshold). Só altera HOWLER (use-sound) e window.Audio.
// =============================================================================
(function aplicarMonkeyPatchGlobalMudo() {
	if (typeof window === "undefined") return;

	// ---------------------------------------------------------------
	// 1) Bloqueia Audio.prototype.play (HTML5 <audio> / new Audio).
	//    Usado por players inline de mídia se alguém colocar autoplay
	//    ou se existir algum componente que nós não mapeamos.
	// ---------------------------------------------------------------
	try {
		const AudioPlayOriginal = window.HTMLMediaElement &&
			window.HTMLMediaElement.prototype.play;
		if (AudioPlayOriginal && typeof AudioPlayOriginal === "function") {
			window.HTMLMediaElement.prototype.play = function playComMudoGlobal() {
				const vGlobal = Number(notificationVolume01);
				const ehMudo = Number.isFinite(vGlobal) && vGlobal <= 0.01;
				if (ehMudo) {
					// NÃO toca. Retorna promise resolved como se tivesse
					// tocado, para não quebrar código que faz .play().then()
					return Promise.resolve();
				}
				return AudioPlayOriginal.apply(this, arguments);
			};
		}
	} catch (_e) { /* noop */ }

	// ---------------------------------------------------------------
	// 2) Bloqueia Howl.prototype.play (lib howler usada POR TRÁS do
	//    use-sound npm). NÃO precisamos importar howler — esperamos o
	//    primeiro setTimeout após o React carregar e aplicamos no
	//    prototype quando a lib estiver no window (quando não tiver,
	//    nenhum problema).
	// ---------------------------------------------------------------
	const patchHowler = () => {
		try {
			const Howl = (window.Howl) ||
				(window.howler && window.howler.Howl);
			if (!Howl || !Howl.prototype || typeof Howl.prototype.play !== "function") return false;
			const playOriginal = Howl.prototype.play;
			// Evitar duplo patch (hot module reload, etc.)
			if (Howl.prototype.__perabit_patched_play__) return true;
			Howl.prototype.play = function playComMudoGlobalComo(sprite) {
				const vGlobal = Number(notificationVolume01);
				const ehMudo = Number.isFinite(vGlobal) && vGlobal <= 0.01;
				if (ehMudo) {
					// NÃO toca. O Howl não emite som mas mantém a instância
					// intacta (para não quebrar listeners de onend etc.).
					return undefined;
				}
				return playOriginal.call(this, sprite);
			};
			Howl.prototype.__perabit_patched_play__ = true;
			return true;
		} catch (_e) {
			return false;
		}
	};

	// Como o Howler é carregado depois (é importado dinamicamente pelo
	// use-sound), tentamos a cada 100ms até 20x (2s de janela). Depois
	// disso para não gastar CPU.
	let tentativas = 0;
	const timer = window.setInterval(() => {
		const ok = patchHowler();
		tentativas++;
		if (ok || tentativas > 20) {
			window.clearInterval(timer);
		}
	}, 100);
})();
